import { useEffect, useMemo, useState } from 'react';
import {
  MARKETPLACE_CATEGORIES,
  type MarketplaceProduct,
  type MarketplaceSort,
} from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useToast } from '@/features/ui/toast/useToast';
import { MarketingNav } from '@/pages/marketing/MarketingNav';
import { MarketingFooter } from '@/pages/marketing/MarketingFooter';
import '@/pages/marketing/marketing.css';
import { CategorySidebar } from '@/features/marketplace/CategorySidebar';
import { PremiumBanner } from '@/features/marketplace/PremiumBanner';
import { ProductCard } from '@/features/marketplace/ProductCard';
import { normalizeProduct } from '@/features/marketplace/normalize';
import '@/features/marketplace/marketplace.css';

export function MarketplacePage() {
  const toast = useToast();
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<MarketplaceSort>('recent');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: err } = await kuunda
        .from('marketplace_products')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(200);

      if (cancelled) return;
      if (err) {
        setProducts([]);
        toast.error(err.message);
      } else {
        setProducts((data ?? []).map((row) => normalizeProduct(row as Record<string, unknown>)));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const displayed = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered = products.filter((p) => {
      if (selectedCategory && p.category !== selectedCategory) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.seller_name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    });
    if (sortBy === 'price-asc') return [...filtered].sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') return [...filtered].sort((a, b) => b.price - a.price);
    if (sortBy === 'rating') return [...filtered].sort((a, b) => b.rating - a.rating);
    return filtered;
  }, [products, searchQuery, sortBy, selectedCategory]);

  const premiumProducts = useMemo(() => {
    return [...products]
      .filter((p) => p.is_premium || p.is_featured)
      .sort((a, b) => Number(b.is_featured) - Number(a.is_featured))
      .slice(0, 5);
  }, [products]);

  return (
    <div className="mkt mkt-page">
      <MarketingNav />
      <div className="mp-layout">
        <header className="mp-header">
          <h1>Marketplace</h1>
          <p>Découvrez les produits et services des entrepreneurs africains</p>
        </header>

        <div className="mp-mobile-tools">
          <div className="mp-search">
            <span className="mp-search-icon" aria-hidden>
              ⌕
            </span>
            <input
              type="search"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Rechercher un produit"
            />
          </div>
          <div className="mp-chip-row">
            <button
              type="button"
              className={`mp-chip${!selectedCategory ? ' is-active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              Tout
            </button>
            {MARKETPLACE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`mp-chip${selectedCategory === cat ? ' is-active' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="mp-grid">
          <aside className="mp-sidebar">
            <CategorySidebar
              selected={selectedCategory}
              onSelect={setSelectedCategory}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              products={products}
            />
          </aside>

          <div>
            <PremiumBanner products={premiumProducts} />

            <div className="mp-toolbar">
              <div className="mp-toolbar-count">
                {selectedCategory ? (
                  <>
                    <strong>{selectedCategory}</strong> — {displayed.length} produit
                    {displayed.length > 1 ? 's' : ''}
                  </>
                ) : (
                  <>
                    {displayed.length} produit{displayed.length > 1 ? 's' : ''} disponible
                    {displayed.length > 1 ? 's' : ''}
                  </>
                )}
              </div>
              <div className="mp-toolbar-sort">
                <select
                  className="mp-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as MarketplaceSort)}
                  aria-label="Trier les produits"
                >
                  <option value="recent">Nouveautés</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="rating">Les mieux notés</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="mp-products">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="mp-skeleton">
                    <div className="mp-skeleton-media" />
                    <div className="mp-skeleton-line" />
                    <div className="mp-skeleton-line short" />
                  </div>
                ))}
              </div>
            ) : displayed.length === 0 ? (
              <div className="mp-empty">
                <div className="mp-empty-icon" aria-hidden>
                  ▤
                </div>
                <p>Aucun produit trouvé</p>
              </div>
            ) : (
              <div className="mp-products">
                {displayed.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
